import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { Card, CardHeader, Button, Input, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@nextui-org/react";
import { FaCompressArrowsAlt } from "react-icons/fa";
import { useMessageContext } from '../../../contexts/MessageContext';
import { MdDelete } from "react-icons/md";

function Droppable({ children, guardar, editados }) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'droppable',
  });
  const [ejercicioTemporal, setEjercicioTemporal] = useState(null);
  const [ejerciciosConfirmados, setEjerciciosConfirmados] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { handleError } = useMessageContext();
  
  // Trackear IDs de ejercicios ya procesados
  const procesadosIdsRef = useRef(new Set());
  const lastChildrenLengthRef = useRef(0);
  const hadEditadosRef = useRef(false);

  // Cargar ejercicios editados
  useEffect(() => {
    if (editados && editados.length > 0) {
      setEjerciciosConfirmados(editados);
      hadEditadosRef.current = true;
    }
  }, [editados]);

  // Resetear cuando el modal padre se cierra (children se vacía Y no hay editados)
  useEffect(() => {
    // Solo resetear si children se vacía Y no estamos en modo edición
    if (children.length === 0 && lastChildrenLengthRef.current > 0 && !hadEditadosRef.current) {
      procesadosIdsRef.current = new Set();
      lastChildrenLengthRef.current = 0;
      setEjerciciosConfirmados([]);
      setEjercicioTemporal(null);
      setShowForm(false);
    }
    // Si editados también se vacía, entonces sí resetear
    if (children.length === 0 && (!editados || editados.length === 0) && hadEditadosRef.current) {
      procesadosIdsRef.current = new Set();
      lastChildrenLengthRef.current = 0;
      hadEditadosRef.current = false;
      setEjerciciosConfirmados([]);
      setEjercicioTemporal(null);
      setShowForm(false);
    }
  }, [children.length, editados]);

  // Mostrar formulario cuando se añade un NUEVO ejercicio (arrastrado)
  useEffect(() => {
    if (children.length > lastChildrenLengthRef.current) {
      const nuevoIndex = children.length - 1;
      const nuevoEjercicio = children[nuevoIndex];
      const uniqueKey = `${nuevoEjercicio.id}-${nuevoIndex}`;
      
      if (!procesadosIdsRef.current.has(uniqueKey)) {
        procesadosIdsRef.current.add(uniqueKey);
        lastChildrenLengthRef.current = children.length;
        setEjercicioTemporal({ ...nuevoEjercicio, _uniqueKey: uniqueKey, series: '', descanso: '', repeticiones: '', notas: '' });
        setShowForm(true);
      }
    }
  }, [children]);

  const handleGuardar = () => {
    if (!ejercicioTemporal?.series ||
      !ejercicioTemporal?.descanso ||
      !ejercicioTemporal?.repeticiones) {
      handleError('Debes completar todos los campos');
      return;
    }

    const { _uniqueKey, ...ejercicioSinKey } = ejercicioTemporal;
    const nuevosEjerciciosConfirmados = [...ejerciciosConfirmados, ejercicioSinKey];
    setEjerciciosConfirmados(nuevosEjerciciosConfirmados);
    guardar(nuevosEjerciciosConfirmados);
    setEjercicioTemporal(null);
    setShowForm(false);
  };

  const handleCancelar = () => {
    setEjercicioTemporal(null);
    setShowForm(false);
  };

  

  return (
    <>
      <div ref={setNodeRef} className='w-full bg-gray-200 dark:bg-gray-800 rounded-xl p-2 relative' style={{ border: isOver ? '2px dashed #00000045' : '2px dashed #00000021', minHeight: '140px' }}>


        <FaCompressArrowsAlt className='text-gray-500 absolute top-0 left-0 right-0 bottom-0 m-auto opacity-20' size={80} />




        {ejerciciosConfirmados.map((ejercicio, index) => (
          <Card key={`${ejercicio.id}-${index}`} className='mb-2 bg-default-100 dark:bg-default-100 w-fit z-10'>

            <CardHeader className='flex items-center gap-2'>
              <p
                className='text-xs text-gray-500 flex justify-center'
              >
                {ejercicio.nombre}
              </p>
              <MdDelete className='text-red-500 cursor-pointer' onClick={() => {
                const nuevosEjercicios = ejerciciosConfirmados.filter((_, i) => i !== index);
                setEjerciciosConfirmados(nuevosEjercicios);
                guardar(nuevosEjercicios);
              }} />
            </CardHeader>
          </Card>
        ))}
      </div>
      {showForm && ejercicioTemporal && ReactDOM.createPortal(
        <Modal 
          isOpen={showForm} 
          onClose={handleCancelar}
          isDismissable={false}
          hideCloseButton
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1">
              Configurar: {ejercicioTemporal.nombre}
            </ModalHeader>
            <ModalBody>
              <Input 
                type="number" 
                label="Cantidad de series" 
                placeholder="3" 
                value={ejercicioTemporal.series}
                onChange={(e) => setEjercicioTemporal({ ...ejercicioTemporal, series: Number(e.target.value) })} 
              />
              <Input 
                type="number" 
                label="Tiempo de descanso (seg)" 
                placeholder="60" 
                value={ejercicioTemporal.descanso}
                onChange={(e) => setEjercicioTemporal({ ...ejercicioTemporal, descanso: Number(e.target.value) })} 
              />
              <Input 
                type="number" 
                label="Cantidad de repeticiones" 
                placeholder="10" 
                value={ejercicioTemporal.repeticiones}
                onChange={(e) => setEjercicioTemporal({ ...ejercicioTemporal, repeticiones: Number(e.target.value) })} 
              />
              <Input 
                type="text" 
                label="Notas del ejercicio" 
                placeholder="Si no tienes una barra, puedes hacerlo con mancuernas" 
                value={ejercicioTemporal.notas}
                onChange={(e) => setEjercicioTemporal({ ...ejercicioTemporal, notas: e.target.value })} 
              />
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={handleCancelar}>
                Cancelar
              </Button>
              <Button color="primary" onPress={handleGuardar}>
                Confirmar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>,
        document.body
      )}
    </>

  );
}

export default Droppable;